import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { fetchRestaurant, updateRestaurant, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function ListingEdit() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [form, setForm] = useState({
    name: "", cuisine: "", neighborhood: "", price_range: "$$",
    short_description: "", long_description: "", cover_image: "",
    address: "", phone: "", website: "", instagram: "",
    tiktok: "", youtube: "", twitter: "", facebook: "", order_url: "", reserve_url: "",
    signature_dishes: "", gallery: "",
  });

  useEffect(() => {
    fetchRestaurant(id)
      .then((r) => {
        if (user && r.owner_user_id !== user.user_id && user.role !== "admin") {
          toast.error("You haven't claimed this listing.");
          navigate(`/directory/${id}`);
          return;
        }
        setForm({
          name: r.name || "", cuisine: r.cuisine || "", neighborhood: r.neighborhood || "",
          price_range: r.price_range || "$$", short_description: r.short_description || "",
          long_description: r.long_description || "", cover_image: r.cover_image || "",
          address: r.address || "", phone: r.phone || "", website: r.website || "",
          instagram: r.instagram || "", tiktok: r.tiktok || "", youtube: r.youtube || "",
          twitter: r.twitter || "", facebook: r.facebook || "", order_url: r.order_url || "",
          reserve_url: r.reserve_url || "",
          signature_dishes: (r.signature_dishes || []).join(", "), gallery: (r.gallery || []).join(", "),
        });
        setLoaded(true);
      })
      .catch(() => navigate("/dashboard"));
  }, [id, user, navigate]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    const payload = {
      ...form,
      signature_dishes: form.signature_dishes.split(",").map((s) => s.trim()).filter(Boolean),
      gallery: form.gallery.split(",").map((s) => s.trim()).filter(Boolean),
    };
    try {
      await updateRestaurant(id, payload);
      toast.success("Listing updated");
      navigate(`/directory/${id}`);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  const inputCls = "w-full border border-sand-300 px-4 py-3 bg-white focus:border-brand-500 outline-none";
  const labelCls = "block font-mono text-[10px] tracking-[0.2em] uppercase text-sand-700 mb-2";

  if (!loaded) return <div className="max-w-2xl mx-auto px-6 py-32 font-mono text-sm text-sand-600">Loading…</div>;

  return (
    <div data-testid="listing-edit-page" className="max-w-2xl mx-auto px-6 py-16 md:py-24">
      <div className="font-mono text-[11px] tracking-[0.3em] uppercase text-brand-500 mb-4">Claimed Listing</div>
      <h1 className="font-serif font-black text-4xl md:text-5xl leading-[1.02] text-sand-900 mb-10">
        Edit your listing.
      </h1>
      <form onSubmit={submit} className="space-y-6">
        {error && (
          <div data-testid="listing-edit-error" className="bg-brand-50 border border-brand-500 text-brand-700 px-4 py-3 text-sm">
            {error}
          </div>
        )}
        <div>
          <label className={labelCls}>Name</label>
          <input data-testid="listing-name-input" required value={form.name} onChange={set("name")} className={inputCls} />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className={labelCls}>Cuisine</label>
            <input data-testid="listing-cuisine-input" value={form.cuisine} onChange={set("cuisine")} className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>Price Range</label>
            <select data-testid="listing-price-select" value={form.price_range} onChange={set("price_range")} className={inputCls}>
              {["$", "$$", "$$$", "$$$$"].map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
          </div>
        </div>
        <div>
          <label className={labelCls}>Neighborhood</label>
          <input data-testid="listing-neighborhood-input" value={form.neighborhood} onChange={set("neighborhood")} className={inputCls} />
        </div>
        <div>
          <label className={labelCls}>Short Description</label>
          <textarea data-testid="listing-short-input" rows={2} value={form.short_description} onChange={set("short_description")} className={inputCls} />
        </div>
        <div>
          <label className={labelCls}>Story / Long Description</label>
          <textarea data-testid="listing-long-input" rows={5} value={form.long_description} onChange={set("long_description")} className={inputCls} />
        </div>
        <div>
          <label className={labelCls}>Cover Image URL</label>
          <input data-testid="listing-cover-input" value={form.cover_image} onChange={set("cover_image")} className={inputCls} placeholder="https://…" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className={labelCls}>Address</label>
            <input data-testid="listing-address-input" value={form.address} onChange={set("address")} className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>Phone</label>
            <input data-testid="listing-phone-input" value={form.phone} onChange={set("phone")} className={inputCls} />
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className={labelCls}>Website</label>
            <input data-testid="listing-website-input" value={form.website} onChange={set("website")} className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>Instagram</label>
            <input data-testid="listing-instagram-input" value={form.instagram} onChange={set("instagram")} className={inputCls} placeholder="@handle" />
          </div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          <div>
            <label className={labelCls}>TikTok</label>
            <input data-testid="listing-tiktok-input" value={form.tiktok} onChange={set("tiktok")} className={inputCls} placeholder="@handle" />
          </div>
          <div>
            <label className={labelCls}>YouTube</label>
            <input data-testid="listing-youtube-input" value={form.youtube} onChange={set("youtube")} className={inputCls} placeholder="@channel" />
          </div>
          <div>
            <label className={labelCls}>X / Twitter</label>
            <input data-testid="listing-twitter-input" value={form.twitter} onChange={set("twitter")} className={inputCls} placeholder="@handle" />
          </div>
          <div>
            <label className={labelCls}>Facebook</label>
            <input data-testid="listing-facebook-input" value={form.facebook} onChange={set("facebook")} className={inputCls} placeholder="page name" />
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className={labelCls}>Order / Delivery Link</label>
            <input data-testid="listing-order-input" value={form.order_url} onChange={set("order_url")} className={inputCls} placeholder="DoorDash, Uber Eats, Toast…" />
          </div>
          <div>
            <label className={labelCls}>Reservation Link</label>
            <input data-testid="listing-reserve-input" value={form.reserve_url} onChange={set("reserve_url")} className={inputCls} placeholder="OpenTable, Resy…" />
          </div>
        </div>
        <div>
          <label className={labelCls}>Signature Dishes (comma separated)</label>
          <input data-testid="listing-signature-input" value={form.signature_dishes} onChange={set("signature_dishes")} className={inputCls} />
        </div>
        <div>
          <label className={labelCls}>Gallery Image URLs (comma separated)</label>
          <input data-testid="listing-gallery-input" value={form.gallery} onChange={set("gallery")} className={inputCls} />
        </div>
        <button
          data-testid="listing-submit-button"
          disabled={busy}
          className="w-full bg-sand-900 text-sand-50 px-5 py-4 font-mono text-[11px] font-bold tracking-[0.2em] uppercase hover:bg-brand-500 transition-colors disabled:opacity-60"
        >
          {busy ? "Saving…" : "Save Changes"}
        </button>
      </form>
    </div>
  );
}
